<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button @click.prevent="goBack" class="sci-btn-back" title="Torna al Gestionale">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <div class="sci-badge-tag">
            <i class="fas fa-stopwatch"></i> Sala & Comande
          </div>
          <h1 class="sci-view-title">{{ $t('marcia') || 'Monitor Marcia Portate & Uscite' }}</h1>
          <p class="sci-view-subtitle">Supervisione in tempo reale del flusso di servizio e tempi di attesa per portata</p>
        </div>
      </div>
      <div class="sci-header-actions">
        <div class="sci-live-pill">
          <span class="live-pulse-dot"></span>
          <span>Auto-refresh 30s</span>
        </div>
        <button class="sci-btn sci-btn-secondary" @click="fetchMarcia" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          <span>Aggiorna</span>
        </button>
      </div>
    </header>

    <!-- Bento KPI Summary Grid -->
    <section class="sci-kpi-grid">
      <div class="sci-kpi-card primary">
        <div class="kpi-icon-wrap blue">
          <i class="fas fa-concierge-bell"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Portate in Marcia</span>
          <span class="kpi-value">{{ marciaItems.length }}</span>
          <span class="kpi-sub">Comande in preparazione</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap green">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">In Orario Regolare</span>
          <span class="kpi-value text-success">{{ countOnTime }}</span>
          <span class="kpi-sub">Entro il 70% del tempo target</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap amber">
          <i class="fas fa-hourglass-half"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">In Scadenza</span>
          <span class="kpi-value text-warning">{{ countWarning }}</span>
          <span class="kpi-sub">Oltre il 70% del tempo target</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap red">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">In Ritardo</span>
          <span class="kpi-value text-danger">{{ countDelayed }}</span>
          <span class="kpi-sub">Tempo massimo superato</span>
        </div>
      </div>
    </section>

    <!-- KDS Display Board Section -->
    <section class="sci-card sci-kds-card">
      <div class="kds-card-header">
        <h3><i class="fas fa-th-large"></i> Tavoli in Marcia & Monitor Uscite</h3>
        <span class="header-hint"><i class="fas fa-clock"></i> Ordinati per priorità di preparazione</span>
      </div>

      <div class="kds-grid-wrapper">
        <div class="kds-grid" v-if="marciaItems.length > 0">
          <div
            v-for="item in marciaItems"
            :key="item.idAdv || (item.sala + '_' + item.tavolo + '_' + item.portata)"
            class="kds-tile"
            :class="'urgency-' + getItemUrgency(item)"
          >
            <!-- Tile Header -->
            <div class="kds-tile-header">
              <div class="table-identifier">
                <span class="table-prefix">TAVOLO</span>
                <span class="table-num">T{{ item.tavolo }}</span>
              </div>
              <span class="sala-tag">
                <i class="fas fa-map-marker-alt"></i> {{ item.sala }}
              </span>
            </div>

            <!-- Course Tag -->
            <div class="course-banner">
              <i class="fas fa-utensil-spoon"></i>
              <span>{{ item.portata }}ª Portata</span>
            </div>

            <!-- Timing Progress & Details -->
            <div class="kds-timing-block">
              <div class="timing-row">
                <span class="time-label">Tempo Trascorso:</span>
                <span class="elapsed-val" :class="'text-' + getItemUrgency(item)">
                  <i class="far fa-clock"></i> {{ getElapsed(item.tm) }} min
                </span>
              </div>

              <div class="progress-bar-wrap">
                <div
                  class="progress-bar-fill"
                  :class="'fill-' + getItemUrgency(item)"
                  :style="{ width: getProgressPercent(item) + '%' }"
                ></div>
              </div>

              <div class="timing-footer">
                <small class="target-val">Target Attesa: <strong>{{ item.tempo_di_attesa }}</strong></small>
                <small class="status-text" :class="'text-' + getItemUrgency(item)">
                  {{ getUrgencyLabel(item) }}
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="loading" class="sci-state-feedback">
          <div class="sci-spinner"></div>
          <p class="state-title">Interrogazione comande in cucina...</p>
        </div>
        <div v-else class="sci-state-feedback">
          <div class="empty-icon-wrap green">
            <i class="fas fa-check-double"></i>
          </div>
          <p class="state-title">Nessun tavolo attualmente in marcia</p>
          <p class="state-subtitle">Tutte le portate delle comande risultano evase o servite regolarmente.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { store } from '../store/store.js';

export default {
  name: 'Marcia',
  data() {
    return {
      marciaItems: [],
      refreshInterval: null,
      loading: false,
      store
    };
  },
  computed: {
    countOnTime() {
      return this.marciaItems.filter(item => this.getItemUrgency(item) === 'normal').length;
    },
    countWarning() {
      return this.marciaItems.filter(item => this.getItemUrgency(item) === 'warning').length;
    },
    countDelayed() {
      return this.marciaItems.filter(item => this.getItemUrgency(item) === 'danger').length;
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'Gestionale' });
    },
    getToken() {
      try {
        if (!localStorage.token) return '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (parsed.token || localStorage.token);
      } catch (e) {
        return localStorage.token || '';
      }
    },
    async fetchMarcia() {
      this.loading = true;
      const token = this.getToken();
      try {
        const response = await this.axios.post(this.store.path + 'displayMarcia', {
          headers: { authorization: `Bearer ${token}` }
        }, {
          headers: { authorization: `Bearer ${token}` }
        });

        if (response.data && response.data.messaggio) {
          this.marciaItems = response.data.messaggio.sort((a, b) => (b.tm || 0) - (a.tm || 0));
        } else {
          this.marciaItems = [];
        }
      } catch (err) {
        console.error("Errore fetch marcia:", err);
      } finally {
        this.loading = false;
      }
    },
    getElapsed(tm) {
      if (!tm) return 0;
      const now = Math.floor(Date.now() / 1000);
      const elapsed = Math.floor((now - tm) / 60);
      return elapsed > 0 ? elapsed : 0;
    },
    getWaitMinutes(item) {
      if (!item.tempo_di_attesa) return 15;
      const parts = String(item.tempo_di_attesa).split(':');
      const mins = parseInt(parts[0], 10) || 0;
      const secs = (parseInt(parts[1], 10) || 0) / 60;
      return mins + secs || 15;
    },
    getItemUrgency(item) {
      const elapsed = this.getElapsed(item.tm);
      const waitMinutes = this.getWaitMinutes(item);
      if (elapsed > waitMinutes) {
        return 'danger';
      } else if (elapsed > waitMinutes * 0.7) {
        return 'warning';
      } else {
        return 'normal';
      }
    },
    getProgressPercent(item) {
      const elapsed = this.getElapsed(item.tm);
      const waitMinutes = this.getWaitMinutes(item);
      const pct = (elapsed / waitMinutes) * 100;
      return Math.min(Math.max(Math.round(pct), 5), 100);
    },
    getUrgencyLabel(item) {
      const urg = this.getItemUrgency(item);
      if (urg === 'danger') return 'Tempo Superato!';
      if (urg === 'warning') return 'In Scadenza';
      return 'Nei Tempi';
    }
  },
  mounted() {
    this.fetchMarcia();
    this.refreshInterval = setInterval(() => {
      this.fetchMarcia();
    }, 30000);
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }
};
</script>

<style scoped>
.sci-view-container {
  min-height: 100vh;
  background-color: #f1f5f9;
  padding: 24px 32px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  box-sizing: border-box;
}

/* Header */
.sci-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sci-btn-back:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-badge-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0284c7;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.sci-view-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.sci-view-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sci-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sci-live-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 8px 14px;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #0284c7;
}

.live-pulse-dot {
  width: 10px;
  height: 10px;
  background-color: #0284c7;
  border-radius: 50%;
  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 8px rgba(2, 132, 199, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-secondary {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* Bento KPI Grid */
.sci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sci-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sci-kpi-card.primary {
  border-left: 4px solid #0284c7;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.kpi-icon-wrap.blue {
  background: #eff6ff;
  color: #2563eb;
}

.kpi-icon-wrap.green {
  background: #f0fdf4;
  color: #16a34a;
}

.kpi-icon-wrap.amber {
  background: #fffbeb;
  color: #d97706;
}

.kpi-icon-wrap.red {
  background: #fef2f2;
  color: #dc2626;
}

.kpi-meta {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0;
}

.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}

.text-success { color: #16a34a !important; }
.text-warning { color: #d97706 !important; }
.text-danger { color: #dc2626 !important; }

/* KDS Card */
.sci-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  overflow: hidden;
}

.kds-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 12px;
}

.kds-card-header h3 {
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

.header-hint {
  font-size: 0.85rem;
  color: #64748b;
}

.kds-grid-wrapper {
  padding: 24px;
  min-height: 380px;
}

.kds-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

/* KDS Tile */
.kds-tile {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 2px solid transparent;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.25s ease;
}

.kds-tile:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.urgency-normal {
  border-color: #10b981;
  background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 40%);
}

.urgency-warning {
  border-color: #f59e0b;
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 40%);
}

.urgency-danger {
  border-color: #ef4444;
  background: linear-gradient(180deg, #fef2f2 0%, #ffffff 40%);
  animation: pulse-danger-border 2s infinite;
}

@keyframes pulse-danger-border {
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  70% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

.kds-tile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.table-identifier {
  display: flex;
  flex-direction: column;
}

.table-prefix {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: #64748b;
}

.table-num {
  font-size: 1.8rem;
  font-weight: 900;
  color: #0f172a;
  line-height: 1;
}

.sala-tag {
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 6px;
}

.course-banner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #1e293b;
  color: #ffffff;
  padding: 6px 14px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 16px;
  align-self: flex-start;
}

.kds-timing-block {
  margin-top: 10px;
}

.timing-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.time-label {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 600;
}

.elapsed-val {
  font-size: 1.1rem;
  font-weight: 800;
}

.elapsed-val.text-normal { color: #059669; }
.elapsed-val.text-warning { color: #d97706; }
.elapsed-val.text-danger { color: #dc2626; }

.progress-bar-wrap {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 50px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 50px;
  transition: width 0.3s;
}

.fill-normal { background: #10b981; }
.fill-warning { background: #f59e0b; }
.fill-danger { background: #ef4444; }

.timing-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
}

.target-val {
  color: #64748b;
}

.status-text {
  font-weight: 700;
  text-transform: uppercase;
}

.status-text.text-normal { color: #10b981; }
.status-text.text-warning { color: #d97706; }
.status-text.text-danger { color: #ef4444; }

/* Feedback States */
.sci-state-feedback {
  padding: 60px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sci-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #0284c7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.empty-icon-wrap.green {
  background: #f0fdf4;
  color: #16a34a;
}

.state-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.state-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}
</style>
